import { defineStore } from "pinia";
import { User, IdTokenResult, Unsubscribe } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  getDocs,
  query,
  collection,
  where,
  onSnapshot,
  updateDoc,
  FirestoreError,
  setDoc
} from "firebase/firestore";

import { getDownloadURL, getStorage, ref } from "firebase/storage";
import router from "../router";

import { useMonedasStore } from "../services/monedas";
import { useUsuarioRolesStore } from "../services/usuarioRoles";
import { useTasasIVAStore } from "../services/tasas";
import { useUsuariosStore } from "../services/usuarios";
import {
  opcionesGeneralesDefecto,
  opcionesGeneralesTicketera,
  IOCAPOSConfig,
  IHandyPOSConfig,
  IFiservPOSConfig,
  IGetnetPOSConfig,
  IScanntechPOSConfig,
  UsuarioSistemaInvitacion,
  usuarioSistemaInvitacionConverter,
  OpcionesGenerales,
  OpcionesGeneralesTicketera,
  IHandyConfig,
  IOCAConfig,
  IFiservConfig,
  IGetnetConfig,
  IScanntechConfig,
  UsuarioEmpresas,
  Usuario,
  usuarioConverter
} from "@fixeduy/restaurantestypes";
import { useProductosCategoriasStore } from "../services/productosCategorias";
import * as Sentry from "@sentry/vue";

export const userUserStore = defineStore({
  id: "userStore",
  persist: true,
  state: () => {
    return {
      userLoggedIn: null as User | null,
      token: null as IdTokenResult | null,
      suscriberSnapshotUsuario: null as Unsubscribe | null,
      suscriberSnapshotEmpresa: null as Unsubscribe | null,
      usuarioMap: {} as Record<string, string>,
      empresa: null as UsuarioEmpresas | null,
      usuario: null as Usuario | null,
      cacheMonedas: 0 as number,
      cacheTasasIVA: 0 as number,
      cacheUsuarios: 0 as number,
      cacheCategoriasProductos: 0 as number,
      opcionesGenerales: opcionesGeneralesDefecto,
      opcionesGeneralesTicketera: opcionesGeneralesTicketera,
      logoPrincipalReferencia: "",
      logoPrincipalURL: "",
      empresasRegistradas: [] as UsuarioEmpresas[],
      googleApi: false as boolean,
      googleApiEmpresasHabilitadas: [] as string[],
      inicioSesionMecanismo: "" as
        | "google"
        | "microsoft"
        | "magicLink"
        | "password"
        | "codigo"
        | "",
      OCAPOSactual: null as IOCAPOSConfig | null,
      handyPOSactual: null as IHandyPOSConfig | null,
      fiservPOSactual: null as IFiservPOSConfig | null,
      getnetPOSactual: null as IGetnetPOSConfig | null,
      scanntechPOSactual: null as IScanntechPOSConfig | null,
      pedidosVistaSector: "pedidos" as "pedidos" | "gestion"
    };
  },
  getters: {
    user(): User | null {
      return this.userLoggedIn;
    }
  },
  actions: {
    async cargarInvitaciones(): Promise<UsuarioSistemaInvitacion[]> {
      const invitacionesEmpresas: UsuarioSistemaInvitacion[] = [];
      if (this.user != null && this.user.email != null) {
        const db = getFirestore();
        const querySnapshot = await getDocs(
          query(
            collection(db, "invitaciones-usuarios"),
            where("mail", "==", this.user.email)
          ).withConverter(usuarioSistemaInvitacionConverter)
        );
        querySnapshot.forEach(doc => {
          invitacionesEmpresas.push(doc.data());
        });
      }
      return invitacionesEmpresas;
    },
    async setEmpresasActual(empresa: UsuarioEmpresas | null) {
      Sentry.setContext("Empresa", {
        id: empresa?.id ?? "",
        rut: empresa?.rut ?? "",
        razon_social: empresa?.razonSocial ?? ""
      });
      this.vaciarDatos();
      this.empresa = empresa;

      if (
        import.meta.env.VITE_ENV_NOMBRE == "Testing" ||
        import.meta.env.VITE_ENV_NOMBRE == "Produccion"
      ) {
        Sentry.setTag("rut", empresa?.rut);
      }

      this.usuario = null;
      const usuarioDocRef = doc(
        getFirestore(),
        `empresas/${this.empresa!.id}/usuarios`,
        this.userLoggedIn!.uid
      ).withConverter(usuarioConverter);
      const usuarioDoc = await getDoc(usuarioDocRef);
      const usuarioData = usuarioDoc.data();
      if (usuarioData != null) {
        this.usuario = usuarioData;
      }
      await this.checkListeners();
    },
    async setOpciones(
      googleApi: boolean,
      googleApiEmpresasHabilitadas: string[]
    ) {
      this.googleApi = googleApi;
      this.googleApiEmpresasHabilitadas = googleApiEmpresasHabilitadas;
    },
    async setOpcionesGenerales(opcion: OpcionesGenerales) {
      const db = getFirestore();
      const empresaDocRef = doc(db, "empresas", this.empresa!.id);
      await setDoc(
        empresaDocRef,
        { opcionesGenerales: opcion },
        { merge: true }
      );
      this.opcionesGenerales = opcion;
    },
    setOpcionesGeneralesTicketera(opciones: OpcionesGeneralesTicketera) {
      const db = getFirestore();
      const empresaDocRef = doc(db, "empresas", this.empresa!.id);
      updateDoc(empresaDocRef, {
        opcionesGeneralesTicketera: opciones
      });
      this.opcionesGeneralesTicketera = opciones;
    },
    setOpcionesHandy(configuracionHandy: IHandyConfig) {
      const db = getFirestore();
      const empresaDocRef = doc(db, "empresas", this.empresa!.id);
      updateDoc(empresaDocRef, {
        "opcionesGenerales.integracionPOSHandy": configuracionHandy
      });
      this.handyPOSactual =
        configuracionHandy.pos.length > 0 ? configuracionHandy.pos[0] : null;
    },
    setOpcionesOCA(configuracionOCA: IOCAConfig) {
      const db = getFirestore();
      const empresaDocRef = doc(db, "empresas", this.empresa!.id);
      updateDoc(empresaDocRef, {
        "opcionesGenerales.integracionPOSOCA": configuracionOCA
      });
      this.OCAPOSactual =
        configuracionOCA.pos.length > 0 ? configuracionOCA.pos[0] : null;
    },
    setOpcionesFiserv(configuracionFiserv: IFiservConfig) {
      const db = getFirestore();
      const empresaDocRef = doc(db, "empresas", this.empresa!.id);
      updateDoc(empresaDocRef, {
        "opcionesGenerales.integracionPOSFiserv": configuracionFiserv
      });
      this.fiservPOSactual =
        configuracionFiserv.pos.length > 0 ? configuracionFiserv.pos[0] : null;
    },
    setOpcionesGetnet(configuracionGetnet: IGetnetConfig) {
      const db = getFirestore();
      const empresaDocRef = doc(db, "empresas", this.empresa!.id);
      updateDoc(empresaDocRef, {
        "opcionesGenerales.integracionPOSGetnet": configuracionGetnet
      });
      this.getnetPOSactual =
        configuracionGetnet.pos.length > 0 ? configuracionGetnet.pos[0] : null;
    },
    setOpcionesScanntech(configuracionScanntech: IScanntechConfig) {
      const db = getFirestore();
      const empresaDocRef = doc(db, "empresas", this.empresa!.id);
      updateDoc(empresaDocRef, {
        "opcionesGenerales.integracionPOSScanntech": configuracionScanntech
      });
      this.scanntechPOSactual =
        configuracionScanntech.pos.length > 0
          ? configuracionScanntech.pos[0]
          : null;
    },
    async checkListeners() {
      await this.checkListenerEmpresa();
      //this.checkListenerUsuario();
      //this.checkListenNotificaciones();

      this.cargarLogo();
    },
    async checkListenerEmpresa() {
      return new Promise<void>((resolve, reject) => {
        if (this.suscriberSnapshotEmpresa == null) {
          const productosCategoriasStore = useProductosCategoriasStore();
          productosCategoriasStore.$hydrate();
          const tasasIVAStore = useTasasIVAStore();
          tasasIVAStore.$hydrate();
          const monedasStore = useMonedasStore();
          monedasStore.$hydrate();
          const usuariosStore = useUsuariosStore();
          usuariosStore.$hydrate();

          if (this.empresa != null) {
            const promises: Promise<any>[] = [];

            this.suscriberSnapshotEmpresa = onSnapshot(
              doc(getFirestore(), "empresas", this.empresa!.id),
              async docResult => {
                const empresa = docResult.data();
                if (empresa == null) return;

                if (this.cacheTasasIVA < empresa.ultimaEdicionTasasIVA) {
                  this.cacheTasasIVA = new Date().getTime();
                  promises.push(tasasIVAStore.getTasasFromFirestore());
                }

                if (
                  this.cacheCategoriasProductos <
                  empresa.ultimaEdicionProductosRubros
                ) {
                  this.cacheCategoriasProductos = new Date().getTime();
                  promises.push(
                    productosCategoriasStore.getCategoriasFromFirestore()
                  );
                }

                if (this.cacheMonedas < empresa.ultimaEdicionMonedas) {
                  this.cacheMonedas = new Date().getTime();
                  promises.push(monedasStore.getMonedasFromFirestore());
                }

                if (this.cacheUsuarios < empresa.ultimaEdicionUsuarios) {
                  this.cacheUsuarios = new Date().getTime();
                  promises.push(usuariosStore.getFromFirestore());
                }
                this.opcionesGenerales = empresa.opcionesGenerales;
                this.opcionesGeneralesTicketera =
                  empresa.opcionesGeneralesTicketera != null
                    ? empresa.opcionesGeneralesTicketera
                    : opcionesGeneralesTicketera;

                if (
                  empresa.opcionesGenerales.integracionHandy != null &&
                  empresa.opcionesGenerales.integracionHandy.habilitado !=
                    null &&
                  empresa.opcionesGenerales.integracionHandy.habilitado &&
                  this.handyPOSactual == null
                ) {
                  // if (this.localActual != null && this.handyPOSactual == null) {
                  //   const posLocalActual =
                  //     empresa.opcionesGenerales.integracionHandy.pos.find(
                  //       (pos: any) =>
                  //         this.localActual != null &&
                  //         pos.local == this.localActual.id
                  //     );
                  //   this.handyPOSactual = posLocalActual[0];
                  // }
                }

                if (
                  empresa.opcionesGenerales.integracionOCA != null &&
                  empresa.opcionesGenerales.integracionOCA.habilitado != null &&
                  empresa.opcionesGenerales.integracionOCA.habilitado &&
                  this.OCAPOSactual == null
                ) {
                  // if (this.localActual != null && this.OCAPOSactual == null) {
                  //   const posLocalActual =
                  //     empresa.opcionesGenerales.integracionOCA.pos.find(
                  //       (pos: any) =>
                  //         this.localActual != null &&
                  //         pos.local == this.localActual.id
                  //     );
                  //   this.OCAPOSactual = posLocalActual[0];
                  // }
                }

                if (
                  empresa.opcionesGenerales.integracionPOSGetnet != null &&
                  empresa.opcionesGenerales.integracionPOSGetnet.habilitado !=
                    null &&
                  empresa.opcionesGenerales.integracionPOSGetnet.habilitado &&
                  this.getnetPOSactual == null
                ) {
                  // if (
                  //   this.localActual != null &&
                  //   this.getnetPOSactual == null
                  // ) {
                  //   const posLocalActual =
                  //     empresa.opcionesGenerales.integracionPOSGetnet.pos.find(
                  //       (pos: any) =>
                  //         this.localActual != null &&
                  //         pos.local == this.localActual.id
                  //     );
                  //   this.getnetPOSactual = posLocalActual[0];
                  // }
                }

                if (
                  empresa.opcionesGenerales.integracionPOSScanntech != null &&
                  empresa.opcionesGenerales.integracionPOSScanntech
                    .habilitado != null &&
                  empresa.opcionesGenerales.integracionPOSScanntech
                    .habilitado &&
                  this.scanntechPOSactual == null
                ) {
                  // if (
                  //   this.localActual != null &&
                  //   this.scanntechPOSactual == null
                  // ) {
                  //   const posLocalActual =
                  //     empresa.opcionesGenerales.integracionPOSScanntech.pos.find(
                  //       (pos: any) =>
                  //         this.localActual != null &&
                  //         pos.localFixed == this.localActual.id
                  //     );
                  //   this.scanntechPOSactual = posLocalActual[0];
                  // }
                }

                if (
                  !("ultimaEdicionProductosRubros" in empresa) ||
                  !("ultimaEdicionTasasIVA" in empresa) ||
                  !("ultimaEdicionMonedas" in empresa) ||
                  !("ultimaEdicionUsuarios" in empresa) ||
                  !("ultimaEdicionEmpresaGestion" in empresa)
                ) {
                  const db = getFirestore();
                  const empresaDocRef = doc(db, "empresas", this.empresa!.id);
                  const time = new Date().getTime();
                  await updateDoc(empresaDocRef, {
                    ultimaEdicionProductosRubros: time,
                    ultimaEdicionTasasIVA: time,
                    ultimaEdicionMonedas: time,
                    ultimaEdicionUsuarios: time
                    // ultimaEdicionEmpresaGestion: 0
                  });
                }
                if (empresa.logoURL == "") {
                  this.logoPrincipalURL = "";
                  this.logoPrincipalReferencia = "";
                } else if (
                  empresa.logoURL != null &&
                  empresa.logoURL != "" &&
                  empresa.logoURL != this.logoPrincipalReferencia
                ) {
                  this.logoPrincipalReferencia = empresa.logoURL;
                }

                Promise.all(promises).then(() => {
                  resolve();
                });
              },
              // 2. Callback para errores (Error) - ¡ESTE ES EL CLAVE!
              (error: FirestoreError) => {
                // Especifica el tipo FirestoreError
                // console.error(`-----------------------------------------------------`);
                // console.error(`[Listener Empresas] ERROR en listener para empresa`);
                // console.error(`[Listener Empresas] Código: ${error.code}`);

                // Comprobación específica para permission-denied
                if (error.code === "permission-denied") {
                  this.signOut();
                } else {
                  // Manejar otros posibles errores del listener
                  console.error("[Listener Empresas] Error inesperado:", error);
                }
              }
              // 3. C
            );
          }
        } else {
          resolve();
        }
      });
    },
    async cargarLogo() {
      if (this.logoPrincipalReferencia != "") {
        try {
          const storage = getStorage();
          const url = await getDownloadURL(
            ref(storage, this.logoPrincipalReferencia)
          );
          this.logoPrincipalURL = url;
        } catch (e) {
          console.log(e);
        }
      }
    },
    setEmpresasRegistradas(empresas: UsuarioEmpresas[]) {
      this.empresasRegistradas = empresas;
    },
    setUser(user: User) {
      this.token = null;
      this.userLoggedIn = user;
    },
    setUserToken(token: IdTokenResult) {
      this.token = token;
    },
    setMecanismoInicioSesion(
      mecanismo: "google" | "microsoft" | "magicLink" | "password" | "codigo"
    ) {
      this.inicioSesionMecanismo = mecanismo;
    },
    setPedidosVistaSector(vista: "pedidos" | "gestion") {
      this.pedidosVistaSector = vista;
    },
    signOut() {
      this.userLoggedIn = null;
      this.token = null;
      this.empresasRegistradas = [];
      if (this.suscriberSnapshotEmpresa != null) {
        this.suscriberSnapshotEmpresa();
        this.suscriberSnapshotEmpresa = null;
      }

      if (this.suscriberSnapshotUsuario != null) {
        this.suscriberSnapshotUsuario();
        this.suscriberSnapshotUsuario = null;
      }
      Sentry.setUser(null);
      this.vaciarDatos();
      router.push("/login");
    },
    vaciarDatos() {
      this.cacheCategoriasProductos = 0;
      this.cacheTasasIVA = 0;
      this.cacheMonedas = 0;
      this.cacheUsuarios = 0;
      this.opcionesGenerales = opcionesGeneralesDefecto;
      this.opcionesGeneralesTicketera = opcionesGeneralesTicketera;
      if (this.suscriberSnapshotEmpresa != null) {
        this.suscriberSnapshotEmpresa();
        this.suscriberSnapshotEmpresa = null;
      }
      this.logoPrincipalReferencia = "";
      this.logoPrincipalURL = "";
      this.empresa = null;

      const productosCategoriasStore = useProductosCategoriasStore();
      productosCategoriasStore.$reset();
      const tasasIVAStore = useTasasIVAStore();
      tasasIVAStore.$reset();
      const monedasStore = useMonedasStore();
      monedasStore.$reset();
      const usuariosStore = useUsuariosStore();
      usuariosStore.$reset();
    }
  }
});
